import React, { useEffect, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import type { ParsedAddress } from "@/utils/address";
import styles from "./AddressAutocompleteInput.module.css";

export type AutocompletedAddress = ParsedAddress;

type PlaceAddressComponent = google.maps.places.AddressComponent;

export function parsePlaceAddressComponents(
  components: PlaceAddressComponent[],
): AutocompletedAddress {
  const findPart = (type: string, name: "longText" | "shortText") =>
    components.find((component) => component.types.includes(type))?.[name] ?? "";
  const streetNumber = findPart("street_number", "longText");
  const route = findPart("route", "longText");

  return {
    addressLine1: [streetNumber, route].filter(Boolean).join(" "),
    addressLine2: findPart("subpremise", "longText"),
    city: findPart("locality", "longText"),
    state: findPart("administrative_area_level_1", "shortText"),
    zip: findPart("postal_code", "shortText"),
    countryCode: findPart("country", "shortText"),
  };
}

export interface AddressAutocompleteInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  name: string;
  onAddressSelect: (address: AutocompletedAddress) => void;
}

/** Google Places address picker using the current Place Autocomplete Element. */
export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  onAddressSelect,
  name,
  value,
  onChange,
  className,
  ...inputProps
}) => {
  const places = useMapsLibrary("places");
  const hostRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
    onChangeRef.current = onChange;
  }, [onAddressSelect, onChange]);

  useEffect(() => {
    const host = hostRef.current;
    if (!places || !host) return;

    const autocomplete = new places.PlaceAutocompleteElement({
      noInputIcon: true,
      noClearButton: true,
      name,
      value: String(value ?? ""),
    });
    autocomplete.className = styles.googleAutocomplete;
    autocomplete.description = String(inputProps["aria-label"] ?? name);
    if (inputProps.id) autocomplete.id = inputProps.id;
    autocomplete.disabled = Boolean(inputProps.disabled);
    autocomplete.setAttribute("aria-invalid", String(Boolean(inputProps["aria-invalid"])));
    host.replaceChildren(autocomplete);
    elementRef.current = autocomplete;

    const handleInput = (event: Event) => {
      const target = autocomplete;
      onChangeRef.current?.({
        target,
        currentTarget: target,
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleSelect = async (event: google.maps.places.PlacePredictionSelectEvent) => {
      const place = event.placePrediction.toPlace();
      const result = await place.fetchFields({ fields: ["addressComponents"] });
      const components = result.place.addressComponents;
      if (!components?.length) return;

      // Localities and points of interest come back without street parts; use the picked
      // suggestion so a half-typed value isn't left in the form.
      const parsed = parsePlaceAddressComponents(components);
      onAddressSelectRef.current({
        ...parsed,
        addressLine1: parsed.addressLine1 || event.placePrediction.mainText?.text || "",
      });
    };

    autocomplete.addEventListener("input", handleInput);
    autocomplete.addEventListener("gmp-select", handleSelect);

    return () => {
      autocomplete.removeEventListener("input", handleInput);
      autocomplete.removeEventListener("gmp-select", handleSelect);
      autocomplete.remove();
      elementRef.current = null;
    };
  }, [places, name, inputProps.id, inputProps["aria-label"], inputProps.disabled]);

  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.disabled = Boolean(inputProps.disabled);
      elementRef.current.setAttribute(
        "aria-invalid",
        String(Boolean(inputProps["aria-invalid"])),
      );
    }
  }, [inputProps.disabled, inputProps["aria-invalid"]]);

  useEffect(() => {
    if (elementRef.current && elementRef.current.value !== String(value ?? "")) {
      elementRef.current.value = String(value ?? "");
    }
  }, [value]);

  if (!places) {
    return (
      <input
        {...inputProps}
        id={inputProps.id}
        name={name}
        value={value}
        onChange={onChange}
        className={className}
        autoComplete="off"
      />
    );
  }

  return (
    <div
      className={styles.host}
      data-testid="place-autocomplete-host"
      aria-invalid={inputProps["aria-invalid"]}
    >
      <div ref={hostRef} className={styles.googleHost} />
      {String(value ?? "").length > 0 && (
        <button
          type="button"
          className={styles.clearButton}
          aria-label="Clear address"
          disabled={Boolean(inputProps.disabled)}
          onClick={() => {
            const autocomplete = elementRef.current;
            if (!autocomplete) return;
            autocomplete.value = "";
            autocomplete.dispatchEvent(new Event("input", { bubbles: true }));
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;